/* eslint-disable brace-style */
// Conflicting with prettier for one curly brace
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormGroup,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AbstractFormDialog,
  AbstractFormDialogData,
  AbstractDialogResultData,
  DIALOG_RETURN_TYPES,
} from '@next/core-lib/dialog';

import { LocaleService } from '@next/core-lib/i18n';
import { filter, map, startWith, Subscription } from 'rxjs';

import {
  CropVariety,
  ProductionPurposeCategory,
  UpsertVarietyPropertiesRequest,
  VarietyProperties,
} from '../../models';
import { decimalValidator } from '../../validators';

export interface CreateVarietyPropertyDialogData extends AbstractFormDialogData {
  cropVarieties: CropVariety[];
  productionPurposeCategories: ProductionPurposeCategory[];
  varietyProperty?: VarietyProperties;
  existingVarietyProperties?: VarietyProperties[];
}

const TWO_DECIMAL_PLACES = 2;
const ONE_DECIMAL_PLACE = 1;

@Component({
  selector: 'app-create-variety-property-dialog',
  templateUrl: './create-variety-property-dialog.component.html',
  styleUrls: ['./create-variety-property-dialog.component.scss'],
  standalone: false,
})
export class CreateVarietyPropertyDialogComponent
  extends AbstractFormDialog<UpsertVarietyPropertiesRequest>
  implements OnInit, OnDestroy
{
  data = inject<CreateVarietyPropertyDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  private readonly locale = inject(LocaleService);
  protected readonly mdDialogRef =
    inject<
      MatDialogRef<
        CreateVarietyPropertyDialogComponent,
        AbstractDialogResultData<UpsertVarietyPropertiesRequest>
      >
    >(MatDialogRef);

  private readonly _groupSubs: Subscription[] = [];
  private _ediCodeSub?: Subscription;
  visibleCategoryIds: number[] = [];
  isEditMode = false;
  orderedCategories: ProductionPurposeCategory[] = [];
  formGroup: FormGroup = this.formBuilder.group({
    ediCode: [null as number | null, Validators.required],
    maturity: [
      null as number | null,
      [
        Validators.min(0.1),
        Validators.max(999.9),
        decimalValidator(ONE_DECIMAL_PLACE),
        Validators.required,
      ],
    ],
  });

  filteredCropVarieties$ = this.formGroup.controls.ediCode.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.filter(value, [...this.data.cropVarieties])),
  );
  formData: UpsertVarietyPropertiesRequest;

  private readonly productionPurposeTranslationKeys: Record<string, string> = {
    CA: 'varietyProperties.productionPurpose.CA',
    PA: 'varietyProperties.productionPurpose.PA',
    FA: 'varietyProperties.productionPurpose.FA',
  };

  displayProductionPurposeName(nameOrCode: string) {
    if (!nameOrCode) return '';
    const key = this.productionPurposeTranslationKeys[nameOrCode];
    return key ? this.locale.translate(key) : nameOrCode;
  }

  ngOnInit(): void {
    this.isEditMode = !!this.data.varietyProperty;
    const categoriesGroup = this.formBuilder.group({});
    const categoriesValidator = (control: AbstractControl): ValidationErrors | null => {
      if (!control || !(control instanceof FormGroup)) {
        return { noCategory: true };
      }
      const keys = Object.keys(control.controls ?? {});
      if (keys.length === 0) {
        return { noCategory: true };
      }
      let anyNonEmpty = false;
      for (const k of keys) {
        const child = control.controls[k] as FormGroup;
        const values = child?.value ? Object.values(child.value) : [];
        const hasAnyValue = values.some((v) => v !== null && v !== undefined && v !== '');
        if (hasAnyValue) {
          anyNonEmpty = true;
          if (child.valid) {
            return null;
          }
        }
      }

      return anyNonEmpty ? { noCategory: true } : null;
    };
    // Field-level conditional 'required' validator: only required if any field in the same category has a value.
    const conditionalRequired = (control: AbstractControl): ValidationErrors | null => {
      const parent = control.parent;
      if (!parent) return null;
      const values = Object.values(parent.value ?? {});
      const anyFilled = values.some((v) => v !== null && v !== undefined && v !== '');
      if (!anyFilled) return null;
      return Validators.required(control);
    };

    for (const cat of this.data.productionPurposeCategories ?? []) {
      categoriesGroup.addControl(
        String(cat.id),
        this.formBuilder.group({
          foliageMax: [
            null as number | null,
            [Validators.min(1), Validators.max(99999), conditionalRequired],
          ],
          day50Percent: [
            null as number | null,
            [
              Validators.min(0.1),
              Validators.max(100),
              decimalValidator(ONE_DECIMAL_PLACE),
              conditionalRequired,
            ],
          ],
          nEfficiency: [
            null as number | null,
            [
              Validators.min(0.01),
              Validators.max(100),
              decimalValidator(TWO_DECIMAL_PLACES),
              conditionalRequired,
            ],
          ],
          nMax: [
            null as number | null,
            [Validators.min(1), Validators.max(99999), conditionalRequired],
          ],
          nNorm: [
            null as number | null,
            [Validators.min(1), Validators.max(99999), conditionalRequired],
          ],
          kMin: [
            null as number | null,
            [Validators.min(1), Validators.max(99999), conditionalRequired],
          ],
          kMax: [
            null as number | null,
            [Validators.min(1), Validators.max(99999), conditionalRequired],
          ],
          giftcorrection: [
            null as number | null,
            [
              Validators.min(0.01),
              Validators.max(1),
              decimalValidator(TWO_DECIMAL_PLACES),
              conditionalRequired,
            ],
          ],
          plantHa: [
            null as number | null,
            [
              Validators.min(0.01),
              Validators.max(1),
              decimalValidator(TWO_DECIMAL_PLACES),
              conditionalRequired,
            ],
          ],
          mgMin: [
            null as number | null,
            [Validators.min(0), Validators.max(99999), conditionalRequired],
          ],
          mgMax: [
            null as number | null,
            [Validators.min(0), Validators.max(99999), conditionalRequired],
          ],
        }),
      );
      // Ensure sibling changes re-run validators on each control so conditionalRequired evaluates.
      const catGroup = categoriesGroup.get(String(cat.id)) as FormGroup;
      const sub = catGroup.valueChanges.pipe(startWith(catGroup.value)).subscribe(() => {
        for (const ctrl of Object.values(catGroup.controls)) {
          ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        }
      });
      this._groupSubs.push(sub);
    }

    categoriesGroup.setValidators(categoriesValidator);
    this.formGroup.addControl('categories', categoriesGroup);

    // In edit mode, populate categories with existing data
    if (this.isEditMode) {
      const existing = this.data.existingVarietyProperties ?? [];
      this.populateCategoriesFromExisting(categoriesGroup, existing);

      if (this.data.varietyProperty) {
        this.formGroup.patchValue({
          ediCode: this.data.varietyProperty.ediCode,
          maturity: this.data.varietyProperty.maturity,
        });
      }

      // Show enabled categories
      this.visibleCategoryIds = Object.keys(categoriesGroup.controls)
        .map(Number)
        .filter((id) => (categoriesGroup.get(String(id)) as FormGroup).enabled);
    } else {
      // In create mode, start with all groups disabled and no visible categories
      for (const cat of this.data.productionPurposeCategories ?? []) {
        const catGroup = categoriesGroup.get(String(cat.id)) as FormGroup | null;
        if (catGroup) {
          catGroup.disable({ emitEvent: false });
        }
      }
      this.visibleCategoryIds = [];
    }

    // Compute a stable ordered list for display: CA, PA, FA (by code), then any others
    const categories = this.data.productionPurposeCategories ?? [];
    const order = ['CA', 'PA', 'FA'];
    this.orderedCategories = [
      ...order.map((code) => categories.find((c) => c.name === code)).filter(Boolean),
      ...categories.filter((c) => !order.includes(c.name)),
    ] as ProductionPurposeCategory[];

    // In create mode, watch for ediCode changes to load existing variety properties
    if (!this.isEditMode) {
      this._ediCodeSub = this.formGroup.controls.ediCode.valueChanges
        .pipe(filter((value) => typeof value === 'number'))
        .subscribe((ediCode: number) => {
          this.loadExistingPropertiesForEdiCode(ediCode, categoriesGroup);
        });
    }
  }

  ngOnDestroy(): void {
    for (const s of this._groupSubs) s.unsubscribe();
    this._groupSubs.length = 0;
    this._ediCodeSub?.unsubscribe();
  }

  addCategory(id: number) {
    const categories = this.formGroup.get('categories') as FormGroup | null;
    if (!categories) return;
    const group = categories.get(String(id)) as FormGroup | null;
    if (!group) return;
    if (group.disabled) group.enable({ emitEvent: false });
    if (!this.visibleCategoryIds.includes(id)) this.visibleCategoryIds.push(id);
  }

  isCategoryActive(id: number): boolean {
    return this.visibleCategoryIds.includes(id);
  }

  private populateCategoriesFromExisting(
    categoriesGroup: FormGroup,
    existing: VarietyProperties[],
  ): void {
    for (const cat of this.data.productionPurposeCategories ?? []) {
      const catGroup = categoriesGroup.get(String(cat.id)) as FormGroup | null;
      if (!catGroup) continue;
      const match = existing.find((p) => p.productionPurposeCategoryId === cat.id);
      if (match) {
        catGroup.patchValue({
          foliageMax: match.foliageMax,
          day50Percent: match.day50Percent,
          nEfficiency: match.nEfficiency,
          nMax: match.nMax,
          nNorm: match.nNorm,
          kMin: match.kMin,
          kMax: match.kMax,
          giftcorrection: match.giftcorrection,
          plantHa: match.plantHa,
          mgMin: match.mgMin,
          mgMax: match.mgMax,
        });
        catGroup.enable({ emitEvent: false });
      } else {
        // No existing values -> disable until the user adds/edits
        catGroup.disable({ emitEvent: false });
      }
    }

    // Update visible categories based on enabled groups
    this.visibleCategoryIds = Object.keys(categoriesGroup.controls)
      .map(Number)
      .filter((id) => (categoriesGroup.get(String(id)) as FormGroup).enabled);
  }

  private loadExistingPropertiesForEdiCode(ediCode: number, categoriesGroup: FormGroup): void {
    // Find existing variety properties for this ediCode from the data
    const existingForEdiCode = (this.data.existingVarietyProperties ?? []).filter(
      (vp) => vp.ediCode === ediCode,
    );

    // Populate categories with the found properties
    this.populateCategoriesFromExisting(categoriesGroup, existingForEdiCode);

    // If we found existing properties, also populate maturity from the first one
    if (existingForEdiCode.length > 0 && existingForEdiCode[0].maturity != null) {
      this.formGroup.patchValue(
        {
          maturity: existingForEdiCode[0].maturity,
        },
        { emitEvent: false },
      );
    }
  }

  displayCropVariety() {
    return (ediCode?: number) =>
      ediCode ? this.data.cropVarieties.find((cv) => cv.ediCode === ediCode)?.name : '';
  }

  confirm(): void {
    if (!this.ensureTopLevelFields()) return;

    const categoriesCtrl = this.formGroup.get('categories');
    if (categoriesCtrl) {
      const categoriesForm = categoriesCtrl as FormGroup;
      const results = this.buildCategoryRequests(categoriesForm);
      if (results.length > 0) {
        this.mdDialogRef.close({
          status: DIALOG_RETURN_TYPES.confirm,
          result: results as unknown as UpsertVarietyPropertiesRequest,
        });
        return;
      }
      categoriesForm.markAsTouched({ onlySelf: true });
      return;
    }

    if (this.formGroup.valid) {
      this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.confirm, result: this.formGroup.value });
    } else {
      // Only mark top-level controls touched (avoid touching all empty category controls).
      this.formGroup.controls.ediCode?.markAsTouched();
      this.formGroup.controls.maturity?.markAsTouched();
    }
  }

  private ensureTopLevelFields(): boolean {
    const ediCode = this.formGroup.controls.ediCode.value as number | null;
    const maturity = this.formGroup.controls.maturity.value as number | null;
    if (!ediCode) {
      this.formGroup.controls.ediCode.markAsTouched();
      return false;
    }
    if (maturity === null || maturity === undefined) {
      this.formGroup.controls.maturity.markAsTouched();
      return false;
    }
    return true;
  }

  private buildCategoryRequests(categoriesCtrl: FormGroup): UpsertVarietyPropertiesRequest[] {
    const ediCode = this.formGroup.controls.ediCode.value as number;
    const maturity = this.formGroup.controls.maturity.value as number;
    const results: UpsertVarietyPropertiesRequest[] = [];
    const existing = (this.data.existingVarietyProperties ?? []).filter((p) => p.ediCode === ediCode);
    for (const cat of this.data.productionPurposeCategories ?? []) {
      const catGroup = categoriesCtrl.get(String(cat.id)) as FormGroup | null;
      const existingMatch = existing.find((p) => p.productionPurposeCategoryId === cat.id);
      if (!catGroup) {
        if (existingMatch) {
          results.push({
            id: existingMatch.id,
            ediCode: ediCode,
            productionPurposeCategoryId: cat.id,
            foliageMax: existingMatch.foliageMax,
            day50Percent: existingMatch.day50Percent,
            nEfficiency: existingMatch.nEfficiency,
            nMax: existingMatch.nMax,
            nNorm: existingMatch.nNorm,
            kMin: existingMatch.kMin,
            kMax: existingMatch.kMax,
            giftcorrection: existingMatch.giftcorrection,
            plantHa: existingMatch.plantHa,
            maturity: maturity,
            mgMin: existingMatch.mgMin,
            mgMax: existingMatch.mgMax,
          });
        }
        continue;
      }

      const formHasAnyValue = Object.values(catGroup.value || {}).some(
        (v) => v !== null && v !== undefined && v !== '',
      );

      // Skip this category when there is no user-entered value and there is no existing record for it.
      // Previously we only skipped for create mode; in edit mode we must also skip truly empty groups
      // unless an existing match exists that should be preserved/updated.
      if (!formHasAnyValue && !existingMatch) continue;

      const fv = catGroup.value;
      const req: UpsertVarietyPropertiesRequest = {
        id: existingMatch?.id,
        ediCode: ediCode,
        productionPurposeCategoryId: cat.id,
        foliageMax: fv.foliageMax ?? existingMatch?.foliageMax,
        day50Percent: fv.day50Percent ?? existingMatch?.day50Percent,
        nEfficiency: fv.nEfficiency ?? existingMatch?.nEfficiency,
        nMax: fv.nMax ?? existingMatch?.nMax,
        nNorm: fv.nNorm ?? existingMatch?.nNorm,
        kMin: fv.kMin ?? existingMatch?.kMin,
        kMax: fv.kMax ?? existingMatch?.kMax,
        giftcorrection: fv.giftcorrection ?? existingMatch?.giftcorrection,
        plantHa: fv.plantHa ?? existingMatch?.plantHa,
        maturity: maturity,
        mgMin: fv.mgMin ?? existingMatch?.mgMin,
        mgMax: fv.mgMax ?? existingMatch?.mgMax,
      };

      results.push(req);
    }
    return results;
  }

  private filter(value: string, items: CropVariety[]) {
    return [...items]
      .sort((a, b) => {
        if (!a.name || !b.name) return 0;
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      })
      .filter((option) => (option.name ?? '').toLowerCase().includes(value.toLowerCase()));
  }
}
